// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Developer/admin-only "view as role" override. Purely cosmetic: it changes which role-gated
// UI the frontend renders, never the token, so the server keeps enforcing the real roles.
//
// The override is stored together with the user id it was picked for. Nothing has to hook
// logout: an entry belonging to another user is simply inert, so logging out or switching
// account drops the simulation without a single extra listener.

import { create } from 'zustand';

export type ViewAsRole = 'developer' | 'admin' | 'user-paid' | 'user' | 'player';
export type RoleOverride = { userId: string; role: ViewAsRole };

const VIEW_AS_ROLES: readonly ViewAsRole[] = ['developer', 'admin', 'user-paid', 'user', 'player'];

type RoleOverrideState = {
  override: RoleOverride | null;
  /**
   * True once storage has been read (immediately on web, after the AsyncStorage read on
   * native) or once a role has been picked. Guards the async hydrate from overwriting a
   * choice the developer made while the read was still in flight.
   */
  hydrated: boolean;
  setOverride: (userId: string, role: ViewAsRole) => void;
  clearOverride: () => void;
};

const STORAGE_KEY = 'goffy:roleOverride';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

// Null on native, where React Native defines `window` but no `localStorage`.
const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

const isViewAsRole = (value: unknown): value is ViewAsRole =>
  typeof value === 'string' && (VIEW_AS_ROLES as readonly string[]).includes(value);

/** Tolerates anything: a hand-edited storage entry must not break the header. */
function parseStoredOverride(raw: string | null): RoleOverride | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RoleOverride>;
    if (typeof parsed?.userId !== 'string' || !parsed.userId) return null;
    if (!isViewAsRole(parsed.role)) return null;
    return { userId: parsed.userId, role: parsed.role };
  } catch {
    return null;
  }
}

/** Synchronous load, so web applies the override before the first render. */
function loadOverride(): RoleOverride | null {
  try {
    const local = getLocalStorage();
    if (!local) return null;
    return parseStoredOverride(local.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Native counterpart; AsyncStorage has no synchronous read. */
async function loadOverrideAsync(): Promise<RoleOverride | null> {
  try {
    if (!asyncStorage) return null;
    return parseStoredOverride(await asyncStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function persistOverride(override: RoleOverride | null) {
  try {
    const local = getLocalStorage();
    if (local) {
      if (override) {
        local.setItem(STORAGE_KEY, JSON.stringify(override));
      } else {
        local.removeItem(STORAGE_KEY);
      }
      return;
    }
    if (!asyncStorage) return;
    void (override
      ? asyncStorage.setItem(STORAGE_KEY, JSON.stringify(override))
      : asyncStorage.removeItem(STORAGE_KEY)
    ).catch(() => undefined);
  } catch {
    // ignore storage failures; keep in-memory state
  }
}

export const useRoleOverrideState = create<RoleOverrideState>((set) => ({
  override: loadOverride(),
  hydrated: !!getLocalStorage(),
  setOverride: (userId, role) => {
    const next = { userId, role };
    persistOverride(next);
    set({ override: next, hydrated: true });
  },
  clearOverride: () => {
    persistOverride(null);
    set({ override: null, hydrated: true });
  },
}));

// Native hydration. One frame (or a few) of the real roles before the stored override
// lands is fine for a developer tool, so nothing gates rendering on `hydrated`.
if (!getLocalStorage() && asyncStorage) {
  void loadOverrideAsync().then((stored) => {
    if (useRoleOverrideState.getState().hydrated) return;
    useRoleOverrideState.setState({ override: stored, hydrated: true });
  });
}
